/**
 * WorkflowOutputs — generic scaffolder output renderer.
 *
 * Shown on every template's task/progress page. Renders status banners and output
 * links/texts. Designed to work correctly across three scenarios:
 *
 * 1. Harness workflow — normal run:
 *    The harnessTriggerAction writes status to oa_pending_approvals_exec_status on every poll
 *    cycle and calls ctx.output() with links/variables on success. Status banners come from
 *    the DB; output links/texts come from the Backstage `output` prop.
 *
 * 2. Harness workflow — ECS crash mid-execution:
 *    The action dies before completing. Backstage permanently marks the task as running
 *    (taskStream.completed stays false, output prop stays empty). The reconciler detects
 *    the stale DB row after 5 minutes, re-polls Harness, writes the correct terminal status
 *    and any output variables back to the DB. The frontend DB poll picks this up within 15s
 *    and shows the correct banner; the DB fallback path renders the output links/texts.
 *
 * 3. Non-Harness workflow (built-in actions, no harnessTriggerAction):
 *    No row is ever written to our DB. dbStatus stays null throughout, so all Harness-specific
 *    banners stay hidden. The "What happens next?" notice is suppressed via taskStream.completed
 *    rather than isTerminal. Output links/texts come from the Backstage `output` prop directly;
 *    the DB fallback fires but returns null (no row) and is silently ignored.
 */

import { ComponentType, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MarkdownContent, Link } from '@backstage/core-components';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import type { ScaffolderTaskOutput } from '@backstage/plugin-scaffolder-react';
import { oneadvancedApiRef } from '@internal/plugin-oneadvanced';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LaunchIcon from '@mui/icons-material/Launch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { WarningOutlined as WarningOutlinedIcon } from '@mui/icons-material';

// Update these URLs as needed
const SERVICENOW_URL = 'https://oneadvanced.service-now.com/esc?id=sc_cat_item&sys_id=941b8637fb81ee10b4edf2c1aeefdce2';
const TEAMS_CHANNEL_URL = 'https://teams.microsoft.com/l/channel/19%3Aea0395adce234596a3fff13eb1f718d7%40thread.tacv2/Questions%20and%20Answers?groupId=f7a4ad0b-b562-4ff0-a64f-81fff30b2ec1&tenantId=2b5a89e7-01bf-42ed-9da1-669d82eec17a'; // replace with direct Q&A channel link

// Statuses that mean the pipeline has finished — stop polling once we reach one of these.
const TERMINAL_STATUSES = new Set([
  'Success', 'ApprovalRejected', 'ApprovalExpired', 'Expired',
  'Failed', 'Errored', 'Aborted', 'TimedOut', 'ConnectionLost',
]);

function resolveIcon(name?: string): React.ReactElement | null {
  if (name === 'launch' || name === 'dashboard' || name === 'open_in_new') {
    return <LaunchIcon fontSize="small" />;
  }
  return null;
}

export const WorkflowOutputs: ComponentType<{ output?: ScaffolderTaskOutput }> = ({
  output,
}) => {
  const { taskId } = useParams<{ taskId: string }>();
  const taskStream = useTaskEventStream(taskId!);
  const api = useApi(oneadvancedApiRef);

  // Only used to determine whether to hide the Backstage log panel on success.
  const succeeded = taskStream.completed && !taskStream.error;

  // ---------------------------------------------------------------------------
  // DB status — drives all banners. Polled every 15s while non-terminal.
  // null when no Harness row exists (non-Harness workflows, or before first write).
  // ---------------------------------------------------------------------------
  const [dbStatus, setDbStatus] = useState<{
    harnessStatus: string;
    pipelineUrl: string | null;
    pipelineName: string | null;
    rejectionReason: string | null;
  } | null>(null);

  const isTerminal = dbStatus ? TERMINAL_STATUSES.has(dbStatus.harnessStatus) : false;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (taskId) {
      const fetchStatus = () => {
        api.toolchainApprovals
          .getExecutionStatus([taskId])
          .then(r => {
            const row = r.data[taskId];
            if (row) setDbStatus(row);
          })
          .catch(() => { /* best-effort */ });
      };

      fetchStatus(); // immediate on mount

      // Poll every 15s so the banner updates live (e.g. approval granted, reconciler fires).
      // For non-Harness workflows the poll returns empty and is a no-op — stops on unmount.
      intervalRef.current = setInterval(fetchStatus, 15_000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [taskId, api]);

  // Stop polling once Harness reaches a terminal state — no further changes expected.
  // Non-Harness workflows never set isTerminal, so polling runs until unmount (harmless no-ops).
  useEffect(() => {
    if (isTerminal && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isTerminal]);

  // Immediately re-poll DB when Backstage marks the task as complete (normal action path).
  // Without this the rejection/success banner wouldn't show until the next 15s poll cycle fires.
  useEffect(() => {
    if (!taskStream.completed || !taskId) return;
    api.toolchainApprovals
      .getExecutionStatus([taskId])
      .then(r => {
        const row = r.data[taskId];
        if (row) setDbStatus(row);
      })
      .catch(() => { /* best-effort */ });
  }, [taskStream.completed, taskId, api]);

  // Derive all display state from DB.
  const status = dbStatus?.harnessStatus;
  const isApprovalWaiting = status === 'ApprovalWaiting' || status === 'InterventionWaiting';
  const isApprovalRejected = status === 'ApprovalRejected';
  const isApprovalExpired  = status === 'ApprovalExpired';
  const isPipelineExpired  = status === 'Expired';
  const isConnectionLost   = status === 'ConnectionLost' || status === 'TimedOut';
  const pipelineUrl        = dbStatus?.pipelineUrl ?? null;
  const rejectionReason    = dbStatus?.rejectionReason ?? undefined;
  // "What happens next?" shown while the workflow is actively running and no specific
  // Harness state has taken over. Three suppression conditions:
  //   isTerminal         — Harness pipeline reached a terminal state (via DB)
  //   isApprovalWaiting  — replaced by the Waiting for Approval banner
  //   taskStream.completed — Backstage marked the task done; covers non-Harness workflows
  //                          that never write to our DB (isTerminal stays false for them)
  const isInProgress = !isTerminal && !isApprovalWaiting && !taskStream.completed;

  // Whether Backstage itself has output to render (links or text from ctx.output() calls).
  const hasBsOutputs = (output?.links?.length ?? 0) > 0 || (output?.text?.length ?? 0) > 0;
  // Fetch DB fallback outputs once the task is done and Backstage has nothing to show.
  // Scenario 1 (normal Harness): hasBsOutputs=true — this never fires.
  // Scenario 2 (crashed Harness): hasBsOutputs=false, isTerminal=true — fires and returns DB outputs.
  // Scenario 3 (non-Harness):     hasBsOutputs=false only if the workflow emitted no outputs;
  //                                fires but returns null (no DB row) — silently ignored.
  const shouldFetchFallback = (taskStream.completed || isTerminal) && !hasBsOutputs;

  const [fallbackOutputs, setFallbackOutputs] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    if (!shouldFetchFallback || !taskId) return;
    api.toolchainApprovals
      .getTaskOutputs(taskId)
      .then(r => setFallbackOutputs(r.data))
      .catch(() => { /* best-effort — silently ignore */ });
  }, [shouldFetchFallback, taskId, api]);

  // Build effective links/texts from Backstage output first, then our fallback.
  const links = output?.links ?? [];
  const texts = output?.text ?? [];

  const fallbackLinks: ScaffolderTaskOutput['links'] = [];
  const fallbackTexts: ScaffolderTaskOutput['text'] = [];
  if (!hasBsOutputs && fallbackOutputs) {
    // In the fallback path we don't have the template schema, so infer the type from the value:
    // anything that looks like a URL becomes a link; everything else becomes a text item.
    for (const [key, value] of Object.entries(fallbackOutputs)) {
      if (value && (value.startsWith('http://') || value.startsWith('https://'))) {
        fallbackLinks.push({ title: key, url: value, icon: 'launch' });
      } else {
        fallbackTexts.push({ title: key, content: value });
      }
    }
  }

  const effectiveLinks = links.length > 0 ? links : fallbackLinks;
  const effectiveTexts = texts.length > 0 ? texts : fallbackTexts;

  const hasOutputs = effectiveLinks.length > 0 || effectiveTexts.length > 0;
  // Outputs are only meaningful on success — never show links/texts after a failure,
  // rejection, or any other non-success terminal state.
  // succeeded      = Backstage marked the task completed with no error (normal action path)
  // status === 'Success' = DB says Harness reported success (covers ECS crash reconciler path
  //                        before/without the SSE completion event being received)
  const isSuccess = succeeded || status === 'Success';
  const hideLogsOnSuccess = isSuccess && hasOutputs;

  return (
    <>
      {hideLogsOnSuccess && (
        <style>{`[data-workflow-outputs-done] ~ * { display: none !important; }`}</style>
      )}
      <Box mt={2} {...(hideLogsOnSuccess ? { 'data-workflow-outputs-done': '' } : {})}>
        {/* Connection lost banner — shown when Backstage stopped monitoring but pipeline may still be running */}
        {isConnectionLost && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <WarningOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Monitoring Stopped
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                Backstage has stopped monitoring this workflow, but your pipeline may still be running in Harness.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: pipelineUrl ? 8 : 12 }}>
                If the approval is actioned, the pipeline will continue and complete automatically — you will need to check Harness directly for the outcome.
              </Typography>
              {pipelineUrl && (
                <Button
                  component="a"
                  href={pipelineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none', marginBottom: 12 }}
                >
                  View pipeline in Harness
                </Button>
              )}
              <Divider style={{ width: '100%', marginBottom: 16 }} />
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                If you experience a problem or need help, raise a support ticket in ServiceNow. For questions or guidance, ask in the SRE &amp; DevOps Q&amp;A channel.
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                <Button
                  component="a"
                  href={SERVICENOW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Raise a support ticket in ServiceNow
                </Button>
                <Button
                  component="a"
                  href={TEAMS_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Ask a question in SRE &amp; DevOps Q&amp;A
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Approval-waiting banner — shown prominently when a Harness pipeline is paused */}
        {isApprovalWaiting && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <WarningOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Waiting for Approval
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                This workflow has reached a <strong>manual approval step</strong> and is currently paused.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                It will continue automatically once the request has been approved — please do not contact the approvers directly. The request will be handled in due course.
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Approval expired banner */}
        {isApprovalExpired && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <WarningOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Approval Expired
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                The approval request was not actioned within the required timeframe and has expired.
              </Typography>
              <Divider style={{ width: '100%', marginBottom: 16 }} />
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                To re-submit this request, simply run this workflow again. If you need help or have questions about next steps, you can raise a support ticket or ask in our Q&amp;A channel.
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                <Button
                  component="a"
                  href={SERVICENOW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Raise a support ticket in ServiceNow
                </Button>
                <Button
                  component="a"
                  href={TEAMS_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Ask a question in SRE &amp; DevOps Q&amp;A
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Pipeline expired banner — Harness execution timeout, not an approval step */}
        {isPipelineExpired && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <WarningOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Pipeline Expired
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                The pipeline exceeded its maximum execution time in Harness and was stopped automatically.
              </Typography>
              <Divider style={{ width: '100%', marginBottom: 16 }} />
              <Typography variant="body2" style={{ marginBottom: 12 }}>
                If you have a <strong>problem</strong> with a workflow, raise a support ticket in ServiceNow.
                If you have a <strong>question</strong> or require <strong>guidance</strong>, ask in the SRE &amp; DevOps Q&amp;A MS Teams channel.
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                <Button
                  component="a"
                  href={SERVICENOW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Raise a support ticket in ServiceNow
                </Button>
                <Button
                  component="a"
                  href={TEAMS_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Ask a question in SRE &amp; DevOps Q&amp;A
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Approval rejected banner — replaces the generic notice on rejection */}
        {isApprovalRejected && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <WarningOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                Approval Rejected
              </Typography>
              <Typography variant="body2" style={{ marginBottom: rejectionReason ? 12 : 4 }}>
                This workflow was stopped because the approval request was rejected.
              </Typography>
              {rejectionReason && (
                <Box
                  style={{
                    background: 'rgba(117,117,117,0.08)',
                    border: '1px solid rgba(117,117,117,0.3)',
                    borderRadius: 4,
                    padding: '10px 20px',
                    marginBottom: 12,
                    textAlign: 'center',
                    display: 'inline-block',
                    minWidth: '30%',
                    maxWidth: '80%',
                  }}
                >
                  <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#757575' }}>
                    Reason
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>{rejectionReason}</Typography>
                </Box>
              )}
              <Divider style={{ width: '100%', marginBottom: 16 }} />

              <Typography variant="body2" style={{ marginBottom: 12 }}>
                If you have questions about why your request was rejected or need guidance on next steps, ask in the SRE &amp; DevOps Q&amp;A channel. If you believe there is a technical problem, raise a support ticket in ServiceNow.
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                <Button
                  component="a"
                  href={SERVICENOW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Raise a support ticket in ServiceNow
                </Button>
                <Button
                  component="a"
                  href={TEAMS_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Ask a question in SRE &amp; DevOps Q&amp;A
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* General workflow notice — shown while in-progress only; suppressed on any terminal outcome */}
        {isInProgress && (
          <Paper
            variant="outlined"
            style={{ padding: '24px', marginBottom: '24px', borderColor: '#757575' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <InfoOutlinedIcon style={{ color: '#757575', marginBottom: 8 }} />
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                What happens next?
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 4 }}>
                Some workflows complete quickly; others may take several minutes depending on the automation involved.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                Certain requests include a manual approval step — processing will pause until that approval is given.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Click <strong>Show Logs</strong> below for more detail on what is happening.
              </Typography>

              <Divider style={{ width: '100%', marginBottom: 16 }} />

              <Typography variant="body2" style={{ marginBottom: 12 }}>
                If you have a <strong>problem</strong> with a workflow, raise a support ticket in ServiceNow.
                If you have a <strong>question</strong> or require <strong>guidance</strong>, ask in the SRE &amp; DevOps Q&amp;A MS Teams channel.
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                <Button
                  component="a"
                  href={SERVICENOW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Raise a support ticket in ServiceNow
                </Button>
                <Button
                  component="a"
                  href={TEAMS_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  style={{ textTransform: 'none' }}
                >
                  Ask a question in SRE &amp; DevOps Q&amp;A
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

      {/* Standard links output — only shown on success */}
      {isSuccess && effectiveLinks.length > 0 && (
        <Paper variant="outlined" style={{ padding: '24px', marginBottom: '16px' }}>
          <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
            {effectiveLinks.map((link, i) => {
              if (link.url) {
                return (
                  <Button
                    key={i}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={resolveIcon(link.icon) ?? <LaunchIcon />}
                    style={{ textTransform: 'none' }}
                  >
                    {link.title ?? link.url}
                  </Button>
                );
              }
              if (link.entityRef) {
                return (
                  <Link
                    key={i}
                    to={`/catalog/${link.entityRef.replace(':', '/')}`}
                  >
                    <Button variant="outlined" style={{ textTransform: 'none' }}>
                      {link.title ?? link.entityRef}
                    </Button>
                  </Link>
                );
              }
              return null;
            })}
          </Box>
        </Paper>
      )}

      {/* Standard text output — only shown on success */}
      {isSuccess && effectiveTexts.map((text, i) => {
        if (!text.content) return null;
        return (
          <Paper key={i} variant="outlined" style={{ padding: '24px', marginBottom: '16px' }}>
            {text.title && (
              <Typography variant="h6" gutterBottom>
                {text.title}
              </Typography>
            )}
            <MarkdownContent content={text.content} />
          </Paper>
        );
      })}
    </Box>
    </>
  );
};
